import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Trophy, Coffee, Plus, Trash2, Save, Sparkles, Users, Coins, BarChart3, Loader2, Star, History,
} from "lucide-react";

/**
 * Phase 24a — Admin Loyalty page.
 * Configure the points engine, manage punch cards, see customer leaderboard
 * + ledger, manually award bonus points.
 */
export default function AdminLoyaltyPage() {
  const [tab, setTab] = useState("config");
  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-loyalty-page">
      <header className="mb-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center">
          <Trophy className="w-5 h-5 text-amber-700" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Loyalty & Rewards</h1>
          <p className="text-sm text-stone-500">Points, punch cards, tiers — give regulars a reason to come back.</p>
        </div>
      </header>

      <div className="flex gap-2 mb-6 flex-wrap" data-testid="admin-loyalty-tabs">
        {[
          { key: "config",   label: "Program",       icon: <Sparkles className="w-3.5 h-3.5" /> },
          { key: "cards",    label: "Punch cards",   icon: <Coffee   className="w-3.5 h-3.5" /> },
          { key: "members",  label: "Members",       icon: <Users    className="w-3.5 h-3.5" /> },
          { key: "ledger",   label: "Activity",      icon: <History  className="w-3.5 h-3.5" /> },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`loyalty-tab-${t.key}`}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm border transition ${
              tab === t.key
                ? "bg-stone-900 text-white border-stone-900"
                : "bg-white border-stone-200 text-stone-700 hover:bg-stone-50"
            }`}
          >
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {tab === "config"  && <ProgramConfig />}
      {tab === "cards"   && <PunchCards />}
      {tab === "members" && <Members />}
      {tab === "ledger"  && <LedgerView />}
    </div>
  );
}

/* ─────────────────────────────  Stats banner  ────────────────────────── */
function StatsBanner() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/loyalty/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);
  if (!stats) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="loyalty-stats">
      <Stat icon={<Users    className="w-4 h-4" />} label="Members"           value={stats.members} />
      <Stat icon={<Coins    className="w-4 h-4" />} label="Outstanding pts"   value={stats.active_points_outstanding.toLocaleString()} />
      <Stat icon={<Sparkles className="w-4 h-4" />} label="Lifetime awarded"  value={stats.lifetime_points_awarded.toLocaleString()} />
      <Stat icon={<BarChart3 className="w-4 h-4" />} label="Redemptions"      value={stats.redemptions} />
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4">
      <div className="flex items-center gap-2 text-stone-500 text-[11px] uppercase tracking-widest">
        {icon}{label}
      </div>
      <div className="text-2xl font-semibold mt-1 text-stone-900">{value}</div>
    </div>
  );
}

/* ─────────────────────────────  Program config  ──────────────────────── */
function ProgramConfig() {
  const [program, setProgram] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/loyalty/program").then((r) => setProgram(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/loyalty/program", {
        is_active: program.is_active,
        program_name: program.program_name,
        points_enabled: program.points_enabled,
        points_per_dollar: Number(program.points_per_dollar),
        points_signup_bonus: Number(program.points_signup_bonus),
        points_birthday_bonus: Number(program.points_birthday_bonus),
        points_redemption_rate: Number(program.points_redemption_rate),
        min_points_to_redeem: Number(program.min_points_to_redeem),
      });
      setProgram(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  if (!program) return <Loader />;
  const set = (k, v) => setProgram({ ...program, [k]: v });

  return (
    <div>
      <StatsBanner />
      <div className="bg-white border border-stone-200 rounded-2xl p-5 sm:p-6 space-y-5" data-testid="loyalty-config">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!program.is_active}
            onChange={(e) => set("is_active", e.target.checked)}
            className="w-4 h-4 rounded border-stone-300"
            data-testid="loyalty-is-active"
          />
          <div>
            <div className="text-sm font-medium text-stone-900">Program is live</div>
            <div className="text-xs text-stone-500">Pause to stop awarding points on new orders.</div>
          </div>
        </label>
        <Row label="Program name">
          <input
            value={program.program_name}
            onChange={(e) => set("program_name", e.target.value)}
            className="rounded-lg border border-stone-200 px-3 py-2 text-sm w-full"
            data-testid="loyalty-program-name"
          />
        </Row>
        <Row label="Points per $1 spent" help="Bronze rate. Higher tiers multiply this.">
          <input
            type="number" step="0.1" min="0"
            value={program.points_per_dollar}
            onChange={(e) => set("points_per_dollar", e.target.value)}
            className="rounded-lg border border-stone-200 px-3 py-2 text-sm w-32"
            data-testid="loyalty-points-per-dollar"
          />
        </Row>
        <Row label="Redemption rate" help="How many points for $1 off?">
          <div className="inline-flex items-center gap-2 text-sm text-stone-500">
            <input
              type="number" min="10"
              value={program.points_redemption_rate}
              onChange={(e) => set("points_redemption_rate", e.target.value)}
              className="rounded-lg border border-stone-200 px-3 py-2 text-sm w-32"
              data-testid="loyalty-redemption-rate"
            /> pts = $1 off
          </div>
        </Row>
        <Row label="Minimum to redeem">
          <input
            type="number" min="10"
            value={program.min_points_to_redeem}
            onChange={(e) => set("min_points_to_redeem", e.target.value)}
            className="rounded-lg border border-stone-200 px-3 py-2 text-sm w-32"
            data-testid="loyalty-min-redeem"
          />
        </Row>
        <Row label="Sign-up bonus (pts)">
          <input
            type="number" min="0"
            value={program.points_signup_bonus}
            onChange={(e) => set("points_signup_bonus", e.target.value)}
            className="rounded-lg border border-stone-200 px-3 py-2 text-sm w-32"
            data-testid="loyalty-signup-bonus"
          />
        </Row>
        <Row label="Birthday bonus (pts)">
          <input
            type="number" min="0"
            value={program.points_birthday_bonus}
            onChange={(e) => set("points_birthday_bonus", e.target.value)}
            className="rounded-lg border border-stone-200 px-3 py-2 text-sm w-32"
            data-testid="loyalty-birthday-bonus"
          />
        </Row>

        <div className="border-t border-stone-100 pt-4">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-3">Tiers</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {(program.tiers || []).map((t) => (
              <div key={t.name} className="rounded-xl border border-stone-200 p-3" data-testid={`loyalty-config-tier-${t.name.toLowerCase()}`}>
                <div className="flex items-center justify-between">
                  <div className="font-medium text-stone-900 flex items-center gap-2"><Star className="w-3.5 h-3.5 text-amber-500" /> {t.name}</div>
                  <span className="text-xs text-stone-500">x{t.multiplier}</span>
                </div>
                <div className="text-[11px] text-stone-500 mt-1">Reach at {t.min_points_year}+ pts/year</div>
                <div className="text-[11px] text-stone-600 mt-1 italic">{t.perk}</div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={save}
          disabled={saving}
          data-testid="loyalty-save-program"
          className="inline-flex items-center gap-2 bg-stone-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save program
        </button>
      </div>
    </div>
  );
}

function Row({ label, help, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-6">
      <div className="sm:w-56 shrink-0">
        <div className="text-sm font-medium text-stone-900">{label}</div>
        {help && <div className="text-xs text-stone-500 mt-0.5">{help}</div>}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

/* ─────────────────────────────  Punch cards  ─────────────────────────── */
function PunchCards() {
  const [program, setProgram] = useState(null);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ name: "", items_required: 5, reward_label: "", category_id: "" });
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    const [p, c] = await Promise.all([
      api.get("/admin/loyalty/program"),
      api.get("/categories").catch(() => ({ data: [] })),
    ]);
    setProgram(p.data);
    setCategories(Array.isArray(c.data) ? c.data : (c.data?.categories || []));
  };
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/loyalty/punch-cards", {
        name: form.name,
        items_required: Number(form.items_required),
        reward_label: form.reward_label,
        category_id: form.category_id || null,
      });
      setForm({ name: "", items_required: 5, reward_label: "", category_id: "" });
      await refresh();
      toast.success("Punch card created");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Create failed");
    } finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this punch card?")) return;
    await api.delete(`/admin/loyalty/punch-cards/${id}`);
    await refresh();
  };

  const toggle = async (card) => {
    await api.patch(`/admin/loyalty/punch-cards/${card.id}`, { is_active: !card.is_active });
    await refresh();
  };

  if (!program) return <Loader />;

  return (
    <div className="space-y-6">
      <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="punch-create">
        <h2 className="text-sm font-semibold text-stone-900 mb-3 flex items-center gap-2"><Plus className="w-4 h-4" /> New punch card</h2>
        <form onSubmit={create} className="grid sm:grid-cols-5 gap-3">
          <input
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Name (e.g. 'Coffee Club')" required
            className="sm:col-span-2 rounded-lg border border-stone-200 px-3 py-2 text-sm"
            data-testid="punch-form-name"
          />
          <input
            type="number" min="2" max="50"
            value={form.items_required} onChange={(e) => setForm({ ...form, items_required: e.target.value })}
            placeholder="Items required" required
            className="rounded-lg border border-stone-200 px-3 py-2 text-sm"
            data-testid="punch-form-count"
          />
          <select
            value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}
            className="rounded-lg border border-stone-200 px-3 py-2 text-sm"
            data-testid="punch-form-category"
          >
            <option value="">— Any category —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <button
            type="submit" disabled={creating || !form.name || !form.reward_label}
            className="rounded-lg bg-stone-900 text-white text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-60"
            data-testid="punch-form-submit"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
          </button>
          <input
            value={form.reward_label} onChange={(e) => setForm({ ...form, reward_label: e.target.value })}
            placeholder="Reward (e.g. '1 free coffee')" required
            className="sm:col-span-5 rounded-lg border border-stone-200 px-3 py-2 text-sm"
            data-testid="punch-form-reward"
          />
        </form>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-stone-900 mb-3">Live punch cards ({(program.punch_cards || []).length})</h2>
        {(program.punch_cards || []).length === 0 ? (
          <div className="text-sm text-stone-500 italic">No punch cards yet — create one above.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(program.punch_cards || []).map((c) => (
              <div
                key={c.id}
                data-testid={`punch-card-${c.id}`}
                className={`bg-white border rounded-2xl p-4 ${c.is_active ? "border-stone-200" : "border-stone-200 opacity-60"}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2 text-stone-900 font-medium">
                      <Coffee className="w-4 h-4 text-amber-600" /> {c.name}
                    </div>
                    <div className="text-xs text-stone-500 mt-0.5">
                      Buy {c.items_required} → {c.reward_label}
                    </div>
                    {c.category_id ? (
                      <div className="text-[10px] uppercase tracking-widest text-stone-400 mt-1">Category-scoped</div>
                    ) : null}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <button
                      onClick={() => toggle(c)}
                      data-testid={`punch-toggle-${c.id}`}
                      className={`text-[11px] px-2.5 py-1 rounded-full border ${c.is_active ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-stone-50 border-stone-200 text-stone-500"}`}
                    >
                      {c.is_active ? "Live" : "Paused"}
                    </button>
                    <button
                      onClick={() => remove(c.id)}
                      data-testid={`punch-delete-${c.id}`}
                      className="p-1.5 rounded-full text-rose-500 hover:bg-rose-50"
                      aria-label="Delete punch card"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex gap-1">
                  {Array.from({ length: c.items_required }).map((_, i) => (
                    <div key={i} className="flex-1 h-1.5 rounded-full bg-stone-200" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────  Members + manual award  ──────────────── */
function Members() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ customer_email: "", points: 100, note: "Gift from us" });
  const [awarding, setAwarding] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/loyalty/customers");
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const award = async (e) => {
    e.preventDefault();
    setAwarding(true);
    try {
      await api.post("/admin/loyalty/award-manual", {
        customer_email: form.customer_email,
        points: Number(form.points),
        note: form.note,
      });
      setForm({ ...form, customer_email: "" });
      await refresh();
      toast.success("Award recorded");
    } catch (e2) {
      toast.error(formatApiError(e2?.response?.data?.detail) || "Failed");
    } finally { setAwarding(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="loyalty-manual-award">
        <h2 className="text-sm font-semibold text-stone-900 mb-3">Manual award / deduct</h2>
        <form onSubmit={award} className="grid sm:grid-cols-4 gap-3">
          <input
            type="email" value={form.customer_email}
            onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
            placeholder="customer@example.com" required
            className="sm:col-span-2 rounded-lg border border-stone-200 px-3 py-2 text-sm"
            data-testid="award-form-email"
          />
          <input
            type="number" value={form.points}
            onChange={(e) => setForm({ ...form, points: e.target.value })}
            placeholder="Points (use - to deduct)"
            className="rounded-lg border border-stone-200 px-3 py-2 text-sm"
            data-testid="award-form-points"
          />
          <button
            type="submit" disabled={awarding}
            className="rounded-lg bg-stone-900 text-white text-sm font-medium flex items-center justify-center gap-1.5 disabled:opacity-60"
            data-testid="award-form-submit"
          >
            {awarding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Award
          </button>
          <input
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="Note (optional)"
            className="sm:col-span-4 rounded-lg border border-stone-200 px-3 py-2 text-sm"
            data-testid="award-form-note"
          />
        </form>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-stone-900 mb-3">Members ({items.length})</h2>
        {loading ? <Loader /> : items.length === 0 ? (
          <div className="text-sm text-stone-500 italic">No members yet — they're created automatically when a customer places their first order.</div>
        ) : (
          <div className="overflow-x-auto bg-white border border-stone-200 rounded-2xl">
            <table className="w-full text-sm" data-testid="loyalty-members-table">
              <thead className="bg-stone-50 text-stone-500 text-[11px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2.5">Email</th>
                  <th className="text-left px-4 py-2.5">Tier</th>
                  <th className="text-right px-4 py-2.5">Balance</th>
                  <th className="text-right px-4 py-2.5">This year</th>
                  <th className="text-right px-4 py-2.5">Lifetime</th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.customer_email} className="border-t border-stone-100">
                    <td className="px-4 py-2.5 text-stone-900">{m.customer_email}</td>
                    <td className="px-4 py-2.5"><TierBadge name={m.tier} /></td>
                    <td className="px-4 py-2.5 text-right font-mono text-stone-900">{m.points_balance.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right text-stone-500">{m.points_earned_this_year.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right text-stone-500">{m.points_earned_lifetime.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TierBadge({ name }) {
  const tone = {
    Bronze: "bg-orange-50 text-orange-700 border-orange-200",
    Silver: "bg-slate-100 text-slate-700 border-slate-200",
    Gold:   "bg-amber-50 text-amber-700 border-amber-200",
    VIP:    "bg-pink-50 text-pink-700 border-pink-200",
  }[name] || "bg-stone-50 text-stone-700 border-stone-200";
  return <span className={`text-[11px] px-2 py-0.5 rounded-full border ${tone}`}>{name || "Bronze"}</span>;
}

/* ─────────────────────────────  Ledger  ──────────────────────────────── */
function LedgerView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/loyalty/ledger").then((r) => {
      setItems(r.data.items || []);
      setLoading(false);
    });
  }, []);

  if (loading) return <Loader />;
  if (items.length === 0) {
    return <div className="text-sm text-stone-500 italic">No loyalty activity yet.</div>;
  }
  return (
    <div className="overflow-x-auto bg-white border border-stone-200 rounded-2xl">
      <table className="w-full text-sm" data-testid="loyalty-ledger-table">
        <thead className="bg-stone-50 text-stone-500 text-[11px] uppercase tracking-widest">
          <tr>
            <th className="text-left px-4 py-2.5">When</th>
            <th className="text-left px-4 py-2.5">Customer</th>
            <th className="text-left px-4 py-2.5">Kind</th>
            <th className="text-right px-4 py-2.5">Points</th>
            <th className="text-left px-4 py-2.5">Note</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row, i) => (
            <tr key={i} className="border-t border-stone-100">
              <td className="px-4 py-2.5 text-stone-500">{new Date(row.created_at).toLocaleString()}</td>
              <td className="px-4 py-2.5 text-stone-900">{row.customer_email}</td>
              <td className="px-4 py-2.5">
                <span className={`text-[11px] px-2 py-0.5 rounded-full border ${
                  row.points >= 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-rose-50 text-rose-700 border-rose-200"
                }`}>{row.kind}</span>
              </td>
              <td className={`px-4 py-2.5 text-right font-mono ${row.points >= 0 ? "text-emerald-700" : "text-rose-600"}`}>
                {row.points > 0 ? "+" : ""}{row.points}
              </td>
              <td className="px-4 py-2.5 text-stone-500 text-xs">{row.note || row.ref_kind}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Loader() {
  return (
    <div className="flex items-center justify-center py-10 text-stone-500">
      <Loader2 className="w-5 h-5 animate-spin" />
    </div>
  );
}
